import React, {useState} from 'react';
import {Button, TextInput, Table, Group, Box, ActionIcon, SegmentedControl, Text, Center} from '@mantine/core';
import {IconEdit, IconArchive, IconArrowBackUp} from '@tabler/icons-react';
import {v4 as uuidv4} from 'uuid';
import StandardModal from '../StandardModal/StandardModal';

export interface Entity {
  id: string;
  name: string;
  archivedAt: string | null;
}

interface Props<T extends Entity> {
  opened: boolean;
  onClose: () => void;
  title: string;
  entityName: string;
  unarchivedEntities: T[];
  archivedEntities: T[];
  actions: {
    upsert: (req: {id: string, name: string, archivedAt?: string | null}) => any;
    archive: (id: string) => any;
    restore: (id: string) => any;
  };
}

export function EntityManagementModal<T extends Entity>({
  opened,
  onClose,
  title,
  entityName,
  unarchivedEntities,
  archivedEntities,
  actions: {upsert, archive, restore}
}: Props<T>) {
  const [editingEntity, setEditingEntity] = useState<Partial<T> | null>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'unarchived' | 'archived'>('unarchived');

  const onSave = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const id = editingEntity?.id || uuidv4();
      const archivedAt = editingEntity?.archivedAt || null;
      await upsert({id, name, archivedAt});
      setEditingEntity(null);
      setName('');
    } catch (error) {
      console.error(`Failed to upsert ${entityName.toLowerCase()}`, error);
    } finally {
      setLoading(false);
    }
  };

  const toggleArchive = async (entity: T) => {
    try {
      if (entity.archivedAt) {
        await restore(entity.id);
      } else {
        await archive(entity.id);
      }
    } catch (error) {
      console.error(`Failed to toggle archive status for ${entityName.toLowerCase()}`, error);
    }
  };

  const startEdit = (entity: T) => {
    setEditingEntity(entity);
    setName(entity.name);
  };

  const startAdd = () => {
    setEditingEntity({});
    setName('');
  };

  const entities = view === 'unarchived' ? unarchivedEntities : archivedEntities;

  return (
    <StandardModal
      opened={opened}
      onCancel={onClose}
      title={title}
      size="md"
      showSubmit={false}
    >
      <Box p="md">
        {editingEntity ? (
          <Box mb="xl">
            <TextInput
              label={editingEntity.id ? `Edit ${entityName} Name` : `New ${entityName} Name`}
              placeholder={`${entityName} Name`}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              required
            />
            <Group position="right" mt="md">
              <Button variant="outline" color="red" onClick={() => setEditingEntity(null)}>Cancel</Button>
              <Button color="green" onClick={onSave} loading={loading}>Save</Button>
            </Group>
          </Box>
        ) : (
          <Box mb="md">
            <Group position="apart" mb="md">
              <Button onClick={startAdd} color="green">
                Add {entityName}
              </Button>
              <SegmentedControl
                value={view}
                onChange={(v: 'unarchived' | 'archived') => setView(v)}
                styles={{label: {marginBottom: 0}}}
                data={[
                  { label: 'Active', value: 'unarchived' },
                  { label: 'Archived', value: 'archived' },
                ]}
              />
            </Group>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{width: 100}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entities.length === 0 ? (
                  <tr>
                    <td colSpan={2}>
                      <Center p="md">
                        <Text color="dimmed">No {view} {entityName.toLowerCase()}s found.</Text>
                      </Center>
                    </td>
                  </tr>
                ) : entities.map((entity: T) => (
                  <tr key={entity.id}>
                    <td>{entity.name}</td>
                    <td>
                      <Group spacing={8}>
                        <ActionIcon onClick={() => startEdit(entity)} color="themePurple" title="Edit">
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          onClick={() => toggleArchive(entity)}
                          color={entity.archivedAt ? "green" : "red"}
                          title={entity.archivedAt ? "Restore" : "Archive"}
                        >
                          {entity.archivedAt ? <IconArrowBackUp size={16} /> : <IconArchive size={16} />}
                        </ActionIcon>
                      </Group>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}
      </Box>
    </StandardModal>
  );
}
